import { differenceInYears, format } from "date-fns";

import { CoursePackSlug, DOB_FORMAT, ProgramOffering } from "@/constants";
import { SurveyAnswers } from "@/contexts";
import { getEctExperiments } from "@/hooks";
import { Partner, User, WeightLossData } from "@/models";
import type { SignupFormData } from "@/models/SignupFormData";
import { ExperimentAssignments } from "@/models/experiments";
import { convertLbToKg } from "@/utils/UnitUtils";
import {
  getAccessDisqualifyingConditions,
  getAccessQualifyingConditions,
  omitCmsAccessPhi,
} from "@/utils/cmsAccess";
import {
  calculatePaceInKgPerWeek,
  getPersonalizedTargetDate,
} from "@/utils/pace";

import { isPersonalizationEnabled } from "./survey/contentPersonalization";

// Height and weight are required to create an account, but we don't show
// Noom Mood enrollees these fields and pass default values instead.
// The actual values aren't important.
const DEFAULT_HEIGHT_FT = 5;
const DEFAULT_HEIGHT_IN = 7;
const DEFAULT_WEIGHT_LB = 150;
const HEIGHT_MULTIPLIER_IN_TO_CM = 2.54;
const WEIGHT_MULTIPLIER_LB_TO_KG = 0.453592;

export const getCoursePacksToAssign = (
  program: ProgramOffering,
  surveyAnswers: SurveyAnswers,
  partnerInfo: Partner | null,
  qualifiedPrograms: ProgramOffering[] = [],
): CoursePackSlug[] => {
  const {
    coursePackSelection = [],
    nutritionBasics,
    eatingMovingAffordably,
    culturalImpacts,
  } = surveyAnswers;

  const coursePacks: CoursePackSlug[] = [];

  if (qualifiedPrograms.includes("DIABETES_LIFESTYLE")) {
    coursePacks.push("DIABETES_SUPPORT");
  }

  if (!["DPP", "MOOD"].includes(program)) {
    if (
      nutritionBasics === "notConfident" ||
      nutritionBasics === "somewhatConfident"
    ) {
      coursePacks.push("NUTRITION_BASICS");
    }
    if (eatingMovingAffordably && !eatingMovingAffordably.includes("none")) {
      coursePacks.push("EAT_AND_MOVE");
    }
    if (culturalImpacts === "always" || culturalImpacts === "occasionally") {
      coursePacks.push("NUTRITION_AND_CULTURE");
    }
  }

  if (isPersonalizationEnabled(partnerInfo, program)) {
    return [...coursePackSelection, ...coursePacks];
  }
  // DPP currently isn't shown content personalization.
  if (program === "DPP" && surveyAnswers.glp1Medication === "yes") {
    return ["GLP1"];
  }
  return coursePacks;
};

const getWeightLossData = (
  program: ProgramOffering,
  surveyAnswers: SurveyAnswers,
): WeightLossData | undefined => {
  if (program === "MOOD") {
    return undefined;
  }

  const targetWeightDate = getPersonalizedTargetDate(surveyAnswers);
  const weightLossSpeedPerWeekInKg = calculatePaceInKgPerWeek(surveyAnswers);
  if (
    surveyAnswers.idealWeight &&
    targetWeightDate &&
    weightLossSpeedPerWeekInKg
  ) {
    return {
      targetWeightInKg: convertLbToKg({
        mainUnitValue: surveyAnswers.idealWeight,
      }).mainUnitValue,
      targetWeightDate: format(targetWeightDate, "yyyy-MM-dd"),
      weightLossSpeedPerWeekInKg,
    };
  }
  return undefined;
};

export const formatAsUserData = ({
  formData,
  b2bUserId,
  program,
  dppTriageKey,
  surveyAnswers,
  policyId,
  coursePacks,
  experiments,
}: {
  formData: SignupFormData;
  b2bUserId: string;
  program: ProgramOffering;
  dppTriageKey?: string;
  surveyAnswers: SurveyAnswers;
  policyId?: string;
  coursePacks: CoursePackSlug[];
  experiments: ExperimentAssignments;
}): User => {
  const {
    address,
    age,
    consentSignature,
    consentStatus,
    dob,
    email,
    employeeId,
    firstName,
    gender,
    heightFeetPortion = DEFAULT_HEIGHT_FT,
    heightInchesPortion = DEFAULT_HEIGHT_IN,
    lastName,
    locale,
    orderScale,
    password,
    phoneNumber: { dialCode, value },
    shippingAddress,
    smsOptIn,
    weight = DEFAULT_WEIGHT_LB,
    mhmdaOptIn,
  } = formData;

  const formattedPhoneNumber = `+${dialCode}${value}`;

  const calculatedAge = dob
    ? differenceInYears(new Date(), dob as Date)
    : (age as number);

  const medicareBeneficiaryId = surveyAnswers.eligibilityCheck?.medicareNumber;

  const userData: User = {
    coursePacks,
    gender,
    locale,
    password,
    surveyAnswers: omitCmsAccessPhi(surveyAnswers),
    age: calculatedAge,
    email: email.trim(),
    heightInCm: getHeightInCm(heightFeetPortion, heightInchesPortion),
    name: firstName.trim(),
    phoneNumber: formattedPhoneNumber,
    weightInKg: getWeightInKg(weight),
    extras: {
      address,
      b2bUserId,
      consentStatus,
      mhmdaOptIn,
      orderScale,
      program,
      smsOptIn,
      consentSignature: consentSignature?.trim(),
      employeeId: employeeId?.trim(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      phoneNumber: formattedPhoneNumber,
    },
  };

  if (dob) {
    userData.extras = {
      ...userData.extras,
      dob: format(dob as Date, DOB_FORMAT),
    };
  }

  if (dppTriageKey) {
    userData.extras = {
      ...userData.extras,
      dppTriageKey,
    };
  }

  if (orderScale) {
    userData.extras = {
      ...userData.extras,
      shippingAddress: { ...(shippingAddress || address) },
    };
  }

  if (policyId) {
    userData.extras = {
      ...userData.extras,
      policyId,
    };
  }

  if (medicareBeneficiaryId) {
    userData.extras = {
      ...userData.extras,
      medicareBeneficiaryId,
    };
  }

  // Only CMS ACCESS members answer the qualifying conditions question.
  if (surveyAnswers.cmsAccessQualifyingConditions) {
    userData.extras = {
      ...userData.extras,
      accessQualifyingConditions: getAccessQualifyingConditions(
        surveyAnswers.cmsAccessQualifyingConditions,
      ),
    };
  }

  // Only CMS ACCESS members answer the exclusions question.
  if (surveyAnswers.cmsAccessDisqualifyingConditions) {
    userData.extras = {
      ...userData.extras,
      accessDisqualifyingConditions: getAccessDisqualifyingConditions(
        surveyAnswers.cmsAccessDisqualifyingConditions,
      ),
    };
  }

  // Only CMS ACCESS members with an overweight BMI answer the waist
  // circumference question.
  if (surveyAnswers.waistCircumference !== undefined) {
    userData.extras = {
      ...userData.extras,
      waistCircumferenceInches: surveyAnswers.waistCircumference,
    };
  }

  /**
   * Only store the medicare question answers if the user stays in Med and is
   * 65+ - we don't need to store these if the user opts out of Med
   *
   * Context: https://noom.slack.com/archives/C06T19HJWD9/p1715291960358249
   */
  if ((program === "MED" || program === "NMPBA") && calculatedAge > 64) {
    userData.extras = {
      ...userData.extras,
      enrolledInMedicare: surveyAnswers?.enrolledInMedicare,
      medicareConsent: surveyAnswers?.medicareConsent,
    };
  }

  if (
    surveyAnswers.liabilityWaiverBMI ||
    surveyAnswers.liabilityWaiverEatingDisorder ||
    surveyAnswers.liabilityWaiverIdealWeight ||
    surveyAnswers.liabilityWaiverPregnant
  ) {
    userData.extras = {
      ...userData.extras,
      liabilityWaiverConsent: "AGREE",
    };
  }

  if (surveyAnswers.createAccount?.ginaAdaConsent) {
    userData.extras = {
      ...userData.extras,
      ginaAdaConsent: "AGREE",
    };
  }

  const ectExperiments = getEctExperiments(experiments);
  if (ectExperiments.length) {
    userData.forceExperimentsToAssign = ectExperiments;
  }

  const weightLossData = getWeightLossData(program, surveyAnswers);
  if (weightLossData) {
    userData.weightLossData = weightLossData;
  }

  return userData;
};

export const getHeightInCm = (
  heightFeetPortion: number,
  heightInchesPortion: number,
): number => {
  return (
    (heightFeetPortion * 12 + heightInchesPortion) * HEIGHT_MULTIPLIER_IN_TO_CM
  );
};

export const getWeightInKg = (weight: number): number => {
  return weight * WEIGHT_MULTIPLIER_LB_TO_KG;
};
